<template>
  <div id="pricing-table-list">
    <!-- Formulaire -->
    <v-dialog
      v-model="isFormActive"
      touchless
      :width="$vuetify.breakpoint.smAndUp ? 700 : '100%'"
    >
      <pricing-table-form
        v-if="isFormActive"
        v-model="item"
        :is-form-active="isFormActive"
        @refetch-data="fetchItems"
      >
        <template v-slot:form-title>
          <span class="font-weight-semibold text-base text--primary">
            {{ item.id ? $t('Modifier la table de tarification') : $t('Ajouter une table de tarification') }}
          </span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="isFormActive = !isFormActive"
          >
            <v-icon size="22">{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </template>
        <template #form-cancel>
          <v-btn
            :loading="loading"
            color="secondary"
            outlined
            type="reset"
            @click="isFormActive = !isFormActive"
          >
            {{ $t('Annuler') }}
          </v-btn>
        </template>
      </pricing-table-form>
    </v-dialog>

    <!-- Liste -->
    <v-card>
      <v-card-title>
        {{ $t('Tables de tarification') }}
        <v-chip
          outlined
          class="ml-2"
        >{{ totalItems }}</v-chip>
      </v-card-title>
      <v-divider class="mt-4" />

      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('Rechercher')"
          outlined
          hide-details
          dense
          class="user-search me-3 mb-4"
        />
        <v-spacer />
        <v-btn
          color="primary"
          class="mb-4 me-3"
          @click.stop="openDialog(false)"
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
          <span>{{ $t('Ajouter une table') }}</span>
        </v-btn>
      </v-card-text>

      <v-data-table
        v-model="selectedRows"
        dense
        :headers="tableColumns"
        :items="items"
        :options.sync="options"
        :page.sync="page"
        :server-items-length="totalItems"
        :loading="loading"
        hide-default-footer
        show-select
        :loading-text="$t('Chargement des éléments')"
        :no-data-text="$t('Pas de données')"
        :no-results-text="$t('Aucun résultat')"
        @page-count="pageCount = $event"
      >
        <!-- Tarification -->
        <template #[`item.pricing`]="{item}">
          <v-icon
            v-if="parsePricing(item.pricing)"
            small
            color="info"
            style="cursor: pointer;"
            @click.stop="openPricingDialog(item)"
          >{{ icons.mdiInformation }}</v-icon>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{item}">
          <v-btn
            icon
            small
          >
            <v-icon
              size="20"
              @click="openDialog(item)"
            >
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="error"
          >
            <v-icon
              size="20"
              color="error"
              @click="confirmDelete(item)"
            >
              {{ icons.mdiDeleteOutline }}
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <v-card-text class="pt-2">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <rows-per-page :options="options" />
          </v-col>
          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="page"
              total-visible="6"
              :length="pageCount"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Dialog détail tarification -->
    <v-dialog
      v-model="pricingDialog"
      max-width="720"
      scrollable
    >
      <v-card v-if="pricingDialogItem">
        <v-card-title class="d-flex align-center py-3">
          <span class="text-base font-weight-semibold">
            {{ pricingDialogItem.Label }}
          </span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="pricingDialog = false"
          >
            <v-icon size="22">{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text
          class="pa-4"
          style="max-height: 60vh; overflow-y: auto;"
        >
          <template v-if="Array.isArray(parsePricing(pricingDialogItem.pricing))">
            <table class="pricing-popup-table">
              <thead>
                <tr>
                  <th
                    v-for="col in Object.keys(parsePricing(pricingDialogItem.pricing)[0] || {})"
                    :key="col"
                  >{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in sortByPageMin(parsePricing(pricingDialogItem.pricing))"
                  :key="i"
                >
                  <td
                    v-for="col in Object.keys(row)"
                    :key="col"
                  >{{ formatPricingValue(col, row[col]) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <template v-else>
            <table class="pricing-popup-table">
              <tbody>
                <tr
                  v-for="(val, key) in parsePricing(pricingDialogItem.pricing)"
                  :key="key"
                >
                  <td class="font-weight-medium key-col">{{ key }}</td>
                  <td>{{ formatPricingValue(key, val) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Confirmation suppression -->
    <v-dialog
      v-model="deleteDialog"
      touchless
      :width="$vuetify.breakpoint.smAndUp ? 300 : '100%'"
    >
      <v-card>
        <v-card-title>{{ $t('Confirmer la suppression') }}</v-card-title>
        <v-divider />
        <v-card-text>{{ $t('Confirmez-vous de vouloir supprimer') }}</v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn
            small
            outlined
            @click="deleteDialog = false"
          >
            {{ $t('Non') }}
          </v-btn>
          <v-spacer />
          <v-btn
            small
            color="primary"
            @click="deleteItem"
          >
            {{ $t('Oui') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="isSnackbarBottomVisible"
      :color="$vuetify.theme.currentTheme.error"
      :light="$vuetify.theme.dark"
    >
      {{ snackBarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiPlus,
  mdiClose,
  mdiDeleteOutline,
  mdiPencilOutline,
  mdiInformation,
} from '@mdi/js'
import { ref, onMounted, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import RowsPerPage from '@/components/RowsPerPage'
import PricingTableForm from './PricingTableForm.vue'
import itemStoreModule from './itemStoreModule'
import controller from './Controller'

const MONETARY_KEYS = ['frais']

export default {
  components: {
    RowsPerPage,
    PricingTableForm,
  },
  setup() {
    const APP_STORE_MODULE_NAME = 'pricingTable'

    if (!store.hasModule(APP_STORE_MODULE_NAME)) {
      store.registerModule(APP_STORE_MODULE_NAME, itemStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(APP_STORE_MODULE_NAME)) {
        store.unregisterModule(APP_STORE_MODULE_NAME)
      }
    })

    onMounted(() => {
      // eslint-disable-next-line no-use-before-define
      fetchItems()
    })

    // --- Helpers tarification ---

    const parsePricing = pricing => {
      if (!pricing) return null
      if (typeof pricing === 'string') {
        try { return JSON.parse(pricing) } catch (e) { return null }
      }
      return pricing
    }

    const formatPricingValue = (key, val) => {
      if (val === null || val === undefined) return '—'
      const num = parseFloat(val)
      if (!isNaN(num)) {
        const formatted = Number.isInteger(num) ? String(num) : num.toFixed(2)
        return MONETARY_KEYS.includes(key) ? `${formatted} €` : formatted
      }
      return val
    }

    const sortByPageMin = rows => {
      if (!Array.isArray(rows)) return rows
      return [...rows].sort((a, b) => {
        const findVal = obj => {
          const k = Object.keys(obj).find(key => key.toLowerCase() === 'page_min')
          return k !== undefined ? parseFloat(obj[k]) || 0 : 0
        }
        return findVal(a) - findVal(b)
      })
    }

    const pricingDialog = ref(false)
    const pricingDialogItem = ref(null)
    const openPricingDialog = pricingTableItem => {
      pricingDialogItem.value = pricingTableItem
      pricingDialog.value = true
    }

    // --- Controller ---

    const {
      item,
      items,
      tableColumns,
      searchQuery,
      totalItems,
      loading,
      options,
      selectedRows,
      isFormActive,
      snackBarMessage,
      isSnackbarBottomVisible,
      openDialog,
      deleteItem,
      confirmDelete,
      deleteDialog,
      fetchItems,
    } = controller()

    return {
      item,
      items,
      tableColumns,
      searchQuery,
      totalItems,
      loading,
      options,
      isFormActive,
      selectedRows,
      snackBarMessage,
      isSnackbarBottomVisible,
      page: 1,
      pageCount: 0,
      deleteDialog,
      confirmDelete,
      openDialog,
      deleteItem,
      fetchItems,
      parsePricing,
      formatPricingValue,
      sortByPageMin,
      pricingDialog,
      pricingDialogItem,
      openPricingDialog,

      icons: {
        mdiPlus,
        mdiClose,
        mdiDeleteOutline,
        mdiPencilOutline,
        mdiInformation,
      },
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/apps/user.scss';

.pricing-popup-table {
  border-collapse: collapse;
  font-size: 0.85rem;
  width: 100%;

  th,
  td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 6px 16px;
    text-align: right;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    background: rgba(0, 0, 0, 0.04);
    text-transform: capitalize;
  }

  td.key-col {
    text-align: left;
  }

  tbody tr:nth-child(even) td {
    background: rgba(0, 0, 0, 0.02);
  }

  tbody tr:hover td {
    background: rgba(0, 0, 0, 0.06);
  }
}
</style>